import React, {
  FC,
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import ButtonGroup from '@atlaskit/button/button-group';
import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import { Checkbox } from '@atlaskit/checkbox';
import { ErrorMessage, Field } from '@atlaskit/form';
import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import RecentIcon from '@atlaskit/icon/glyph/recent';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import ModalDialog, {
  ModalBody,
  ModalHeader,
  ModalTitle,
} from '@atlaskit/modal-dialog';
import { Flex } from '@atlaskit/primitives';
import TextArea from '@atlaskit/textarea';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import RefBranch from 'src/components/ref-label/src/refs/ref-branch';
import { PullRequest } from 'src/components/types';
import { getName } from 'src/components/user-profile';
import { useIntl } from 'src/hooks/intl';
import { commonMessages } from 'src/i18n';
import { PR_MERGE_INTERACTION, startMetric } from 'src/metrics';
import { transitionIssues } from 'src/redux/jira/actions';
import {
  getIssueTransitionForm,
  getPullRequestJiraIssuesByType,
} from 'src/redux/jira/selectors/jira-issue-selectors';
import {
  cancelPendingMerge as cancelPendingMergeAction,
  closeDialog,
  createPendingMerge as createPendingMergeAction,
  getIsMergeErrored,
  getIsMergeRequesting,
  getIsRequestingPendingMerge,
  getMergeErrorCode,
  getMergeErrorMessage,
  getMergeInfo,
  getStackedPullRequestsCount,
  requestMerge,
} from 'src/redux/pull-request/merge-reducer';
import {
  getCanDeleteSourceBranch,
  getConflictStatus,
  getCurrentPullRequest,
  getCurrentPullRequestApprovers,
  getDefaultMergeStrategy,
  getIRAnalyticsTrackingAttributes,
  getIsEnforcedMergeChecksEnabled,
} from 'src/redux/pull-request/selectors';
import pullRequestUrls from 'src/redux/pull-request/urls';
import { getCurrentRepoCommits } from 'src/redux/repo-commits/selectors';
import { StrategySelect } from 'src/sections/repository/components/strategy-select';
import IssueTransitionForm from 'src/sections/repository/sections/pull-request/components/issue-transition-form';
import urls from 'src/sections/repository/urls';
import { getStrategyOption } from 'src/sections/repository/utils/merge-strategies';
import { getIsSiteMessageActive } from 'src/selectors/global-selectors';
import { FakeLabel } from 'src/styles';
import { MergeInfo, MergeStrategy } from 'src/types/pull-request';
import { BucketState } from 'src/types/state';
import { publishTrackEvent, publishUiEvent } from 'src/utils/analytics/publish';
import { getBranchesFullNames } from 'src/utils/compare-branches';
import authRequest from 'src/utils/fetch';

import { useMergeData } from '../../hooks/merge-data';
import { ForgeEnabledOnMergeChecks } from '../merge-checks-card/forge-enabled-on-merge-checks';
import {
  DiffStateContext,
  SelectedDiffType,
} from '../tabs/diff/diff-state-context';

import { mergeMessageMaxSizeBytes } from './constants';
import messages from './merge-dialog.i18n';
import * as styles from './merge-dialog.style';
import { MergeWarningSectionMessage } from './merge-warning-section-message';
import PendingReviewersInfo from './pending-reviewers-info';
import WarningMergeDialog from './warning-merge-dialog';

/* eslint @typescript-eslint/ban-types: "warn" */
export const MergeDialog: FC = () => {
  const approvers = useSelector(getCurrentPullRequestApprovers);
  const currentPullRequest = useSelector(getCurrentPullRequest) as PullRequest;
  const commits = useSelector(getCurrentRepoCommits);
  const isRequesting = useSelector(getIsMergeRequesting);
  const isRequestingPendingMerge = useSelector(getIsRequestingPendingMerge);
  const isErrored = useSelector(getIsMergeErrored);
  const hasConflicts = useSelector(getConflictStatus);
  const errorMessage = useSelector(getMergeErrorMessage);
  const errorCode = useSelector(getMergeErrorCode);
  const mergeInfo = useSelector(getMergeInfo);
  const defaultMergeStrategy = useSelector(
    getDefaultMergeStrategy
  ) as MergeStrategy;
  const isBannerOpen = useSelector(getIsSiteMessageActive);
  const stackedPullRequestsCount = useSelector(getStackedPullRequestsCount);
  const canDeleteSourceBranch = useSelector(
    getCanDeleteSourceBranch
  ) as boolean;
  const issueTransitionForm = useSelector(getIssueTransitionForm);
  const pullRequestJiraIssues = useSelector((state: BucketState) =>
    getPullRequestJiraIssuesByType(state, 'PrIssue')
  );
  const isEnforcedMergeChecksEnabled = useSelector(
    getIsEnforcedMergeChecksEnabled
  );

  const { iterativeReviewSelectedDiff } = useContext(DiffStateContext);
  const irTracking = useSelector(getIRAnalyticsTrackingAttributes);
  irTracking.irSelectedDiffType =
    SelectedDiffType[iterativeReviewSelectedDiff.selectedDiffType];
  const dispatch = useDispatch();
  const onClose = () => dispatch(closeDialog());

  const intl = useIntl();
  const {
    canCreatePendingMerge,
    isMergePending,
    mergeChecksIsMergeable,
    forgeMergeChecksData,
  } = useMergeData();

  const branchMergeStrategiesList =
    currentPullRequest.destination.branch.merge_strategies;

  // Only include strategies defined in the MergeStrategy enum
  const filteredMergeStrategies = branchMergeStrategiesList.filter(
    (strategy: string) =>
      Object.values(MergeStrategy).includes(strategy as MergeStrategy)
  );

  const availableMergeStrategies = useRef<MergeStrategy[]>(
    filteredMergeStrategies
  );

  const getInitialState = () => {
    let validDefaultMergeStrategy = branchMergeStrategiesList[0];

    if (
      defaultMergeStrategy &&
      branchMergeStrategiesList.includes(defaultMergeStrategy)
    ) {
      validDefaultMergeStrategy = defaultMergeStrategy;
    }

    const {
      closeSourceBranch = currentPullRequest.close_source_branch,
      mergeStrategy = validDefaultMergeStrategy || MergeStrategy.MergeCommit,
      message = getDefaultCommitMessage(
        validDefaultMergeStrategy || MergeStrategy.MergeCommit
      ),
    }: MergeInfo = mergeInfo;

    return {
      closeSourceBranch,
      mergeStrategy,
      message,
    };
  };

  const [closeSourceBranch, setCloseSourceBranch] = useState(
    getInitialState().closeSourceBranch
  );
  const [mergeStrategy, setMergeStrategy] = useState(
    getInitialState().mergeStrategy
  );
  const [message, setMessage] = useState(getInitialState().message);
  const [isMessageInvalid, setIsMessageInvalid] = useState(false);
  const [isOpenWarningMergeDialog, setIsOpenWarningMergeDialog] =
    useState(false);
  const [openSourceBranchPullRequests, setOpenSourceBranchPullRequests] =
    useState(false);

  useEffect(() => {
    (async () => {
      const { source, destination } = currentPullRequest;
      const sourceRepo = source.repository;
      const destRepo = destination.repository;

      // checking if source and dest repos exist
      if (!sourceRepo || !destRepo) {
        return;
      }

      // count pull requests with the same source branch in the current repo
      const sourceBranchName = source.branch.name;
      let pullRequestCount = await countSourceBranchPullRequests(
        destRepo.full_name,
        sourceRepo.full_name,
        sourceBranchName
      );

      let additionalPullRequests = 0;

      // if pr is between a fork and parent, add pull requests with the same
      // source branch in the child repo
      if (sourceRepo !== destRepo) {
        additionalPullRequests = await countSourceBranchPullRequests(
          sourceRepo.full_name,
          sourceRepo.full_name,
          sourceBranchName
        );

        // if pr is between branches in the same repo, check if there is a parent repo
        // and add pull requests with the same source branch in the parent repo
      } else if (sourceRepo.parent) {
        additionalPullRequests = await countSourceBranchPullRequests(
          sourceRepo.parent.full_name,
          sourceRepo.full_name,
          sourceBranchName
        );
      }

      pullRequestCount += additionalPullRequests;
      const hasOtherPullRequests = pullRequestCount > 1;

      setOpenSourceBranchPullRequests(hasOtherPullRequests);
      if (hasOtherPullRequests) {
        setCloseSourceBranch(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prevDefaultMergeStrategy = useRef(defaultMergeStrategy);

  useEffect(() => {
    if (!prevDefaultMergeStrategy.current && defaultMergeStrategy) {
      updateMergeStrategy(defaultMergeStrategy as MergeStrategy);
    }
    prevDefaultMergeStrategy.current = defaultMergeStrategy;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultMergeStrategy]);

  // returns the number of pull requests in the given repo
  // with the given source branch,
  async function countSourceBranchPullRequests(
    destRepoFullSlug: string,
    sourceRepoFullSlug: string,
    sourceBranchName: string
  ): Promise<number> {
    const url = pullRequestUrls.api.v20.sourceBranchPullRequests(
      destRepoFullSlug,
      sourceRepoFullSlug,
      sourceBranchName
    );

    const response = await fetch(authRequest(url));
    if (!response.ok) {
      // allow user to close source branch if the request fails
      return 0;
    }
    const json = await response.json();
    return json.size;
  }

  function getDefaultCommitMessage(mergeStrategyArg: MergeStrategy) {
    if (
      mergeStrategyArg === MergeStrategy.FastForward ||
      mergeStrategyArg === MergeStrategy.RebaseFastForward
    ) {
      return '';
    }

    const { id, source, title } = currentPullRequest;

    const mergedInLabel = intl.formatMessage(messages.mergedInLabel);
    const approvedByLabel = intl.formatMessage(messages.approvedByLabel);
    const pullRequestLabel = intl.formatMessage(messages.pullRequestLabel);

    const branchInfo = `${mergedInLabel} ${
      source && source.branch && source.branch.name
    }`;
    const pullRequestInfo = `(${pullRequestLabel} #${id})`;

    let defaultCommitMessage = `${branchInfo} ${pullRequestInfo}\n\n${title}`;

    if (
      mergeStrategyArg === MergeStrategy.Squash ||
      mergeStrategyArg === MergeStrategy.SquashFastForward
    ) {
      // Minor optimization: if there's exactly 1 commit, and the commit
      // message already matches the pull request title, no need to display the
      // same text twice.
      if (commits && (commits.length !== 1 || commits[0].message !== title)) {
        const commitMessages = [...commits]
          .reverse()
          .map(commit => `* ${commit.message}`)
          .join('\n');
        defaultCommitMessage += `\n\n${commitMessages}`;
      }
    }

    if (approvers.length > 0) {
      const approverInfo = approvers
        .map(approver => `${approvedByLabel} ${getName(approver.user, intl)}`)
        .join('\n');
      defaultCommitMessage += `\n\n${approverInfo}`;
    }

    return defaultCommitMessage;
  }

  const isMessageValid = () => {
    if (mergeMessageMaxSizeBytes === undefined) {
      return true;
    }

    const encoder = new TextEncoder();
    const messageSize = encoder.encode(message).length;

    const isValid = messageSize <= mergeMessageMaxSizeBytes;
    setIsMessageInvalid(!isValid);
    return isValid;
  };

  const submit = () => {
    if (!isMessageValid()) {
      return;
    }

    const numIssuesToTransition = issueTransitionForm.filter(
      transition => transition.shouldTransition
    ).length;

    startMetric(PR_MERGE_INTERACTION);

    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'mergeButton',
      source: 'pullRequestScreen',
      attributes: {
        prIssuesCount: pullRequestJiraIssues.length,
        prTransitionsCount: numIssuesToTransition,
        frontbucketView: true,
        irTracking,
      },
    });

    if (numIssuesToTransition > 0) {
      dispatch(transitionIssues());
    }

    dispatch(
      requestMerge({
        closeSourceBranch,
        mergeStrategy,
        message,
      })
    );

    onClose();
  };

  const createPendingMerge = () => {
    dispatch(
      createPendingMergeAction({
        closeSourceBranch,
        mergeStrategy,
        message,
      })
    );
  };

  const cancelPendingMerge = () => dispatch(cancelPendingMergeAction());

  const hideWarningDialog = () => setIsOpenWarningMergeDialog(false);

  // @ts-ignore TODO: fix noImplicitAny error here
  const updateCommitMessage = event => {
    setMessage(event.target.value);
  };

  // @ts-ignore TODO: fix noImplicitAny error here
  const updateCloseSourceBranch = event => {
    setCloseSourceBranch(event.target.checked);
  };

  function updateMergeStrategy(newMergeStrategy: MergeStrategy) {
    if (newMergeStrategy === mergeStrategy) {
      return;
    }

    setMergeStrategy(newMergeStrategy);
    setMessage(getDefaultCommitMessage(newMergeStrategy));
  }

  // @ts-ignore TODO: fix noImplicitAny error here
  const handleMergeStrategyChange = selectedItem => {
    if (selectedItem) {
      updateMergeStrategy(selectedItem.value);
    }
  };

  const renderMergeButton = () => {
    const isTransitionable =
      issueTransitionForm.filter(
        transition =>
          transition.shouldTransition && !transition.selectedTransition
      ).length === 0;
    const iconBefore = canCreatePendingMerge ? (
      <RecentIcon size="small" label="Pending" />
    ) : undefined;

    if (hasConflicts) {
      return renderDisabledEnforcedMergeButton(
        messages.mergeIssueConflictsTooltip
      );
    } else if (!mergeChecksIsMergeable && !canCreatePendingMerge) {
      return renderDisabledEnforcedMergeButton(messages.mergeWarningTooltip);
    } else if (!isTransitionable) {
      return renderDisabledEnforcedMergeButton(
        messages.mergeIssueTransitionWarningTooltip
      );
    }

    return (
      <CustomThemeButton
        appearance="primary"
        iconBefore={iconBefore}
        isDisabled={isRequesting || isRequestingPendingMerge}
        isLoading={isRequesting}
        onClick={
          !isEnforcedMergeChecksEnabled && canCreatePendingMerge
            ? () => setIsOpenWarningMergeDialog(true)
            : canCreatePendingMerge
            ? createPendingMerge
            : submit
        }
      >
        <styles.InnerButtonText data-qa="merge-dialog-merge-button">
          <FormattedMessage {...messages.mergePullRequestAction} />
        </styles.InnerButtonText>
      </CustomThemeButton>
    );
  };

  const renderCancelPendingMergeButton = () => {
    return (
      <CustomThemeButton
        appearance="default"
        iconBefore={<RecentIcon size="small" label="" />}
        label={intl.formatMessage(messages.cancelPendingMergeAction)}
        onClick={cancelPendingMerge}
      >
        <styles.InnerButtonText>
          <FormattedMessage {...messages.cancelPendingMergeAction} />
        </styles.InnerButtonText>
      </CustomThemeButton>
    );
  };

  function renderDisabledEnforcedMergeButton(
    messageDescriptor: FormattedMessage.MessageDescriptor
  ) {
    return (
      <Tooltip
        position="top"
        content={
          <styles.TooltipContent>
            {intl.formatMessage(messageDescriptor)}
          </styles.TooltipContent>
        }
      >
        <CustomThemeButton
          appearance="primary"
          isDisabled
          label={intl.formatMessage(messages.mergePullRequestAction)}
          onClick={submit}
        >
          <styles.InnerButtonText data-qa="merge-dialog-merge-button">
            <FormattedMessage {...messages.mergePullRequestAction} />
          </styles.InnerButtonText>
        </CustomThemeButton>
      </Tooltip>
    );
  }

  const { source, destination } = currentPullRequest;

  const fullNames = getBranchesFullNames(
    source.branch.name,
    destination.branch.name,
    `${source?.repository?.workspace?.slug}/${source?.repository?.slug}`,
    `${destination?.repository?.workspace?.slug}/${destination?.repository?.slug}`
  );

  const fullSourceBranchName = fullNames.sourceBranchName;
  const fullDestBranchName = fullNames.destinationBranchName;
  const isSameWs =
    source?.repository?.workspace?.slug ===
    destination?.repository?.workspace?.slug;
  const isFromFork =
    (isSameWs && source?.repository?.slug !== destination?.repository?.slug) ||
    !isSameWs;

  const enabledOnMergeChecksProps = forgeMergeChecksData?.enabledOnMergeChecks;

  const shouldRemoveCommitMessageDiv =
    mergeStrategy === MergeStrategy.FastForward ||
    mergeStrategy === MergeStrategy.RebaseFastForward;

  const closeBranchMessage = () => {
    if (stackedPullRequestsCount > 0 && source.repository) {
      const [owner, slug] = source.repository.full_name.split('/');
      return (
        <FormattedMessage
          {...messages.closeSourceBranchAndRetargetStackedPullRequests}
          values={{
            stackedPullRequests: (
              <a
                target="_blank"
                href={urls.ui.pullRequestsTargetingBranch(
                  owner,
                  slug,
                  source.branch.name
                )}
              >
                <FormattedMessage
                  {...messages.stackedPullRequestsLink}
                  values={{
                    count: stackedPullRequestsCount,
                  }}
                />
              </a>
            ),
          }}
        />
      );
    } else {
      return <FormattedMessage {...messages.closeSourceBranch} />;
    }
  };

  const closeSourceBranchCheckbox = (
    <Checkbox
      isChecked={canDeleteSourceBranch && closeSourceBranch}
      isDisabled={
        !canDeleteSourceBranch ||
        openSourceBranchPullRequests ||
        isMergePending ||
        isRequesting
      }
      label={closeBranchMessage()}
      onChange={updateCloseSourceBranch}
      name="close-source-branch-checkbox"
      value={intl.formatMessage(messages.closeSourceBranch)}
    />
  );

  const closeSourceBranchCheckboxWrapper = openSourceBranchPullRequests ? (
    <Tooltip
      content={intl.formatMessage(messages.otherSourceBranchPullRequests)}
      position="bottom"
    >
      {closeSourceBranchCheckbox}
    </Tooltip>
  ) : (
    closeSourceBranchCheckbox
  );

  const Footer = () => (
    <styles.ModalFooter>
      <div>{closeSourceBranchCheckboxWrapper}</div>
      <ButtonGroup>
        {isMergePending
          ? renderCancelPendingMergeButton()
          : renderMergeButton()}
        <CustomThemeButton
          appearance="subtle"
          onClick={onClose}
          isDisabled={isRequesting}
          label={intl.formatMessage(commonMessages.cancel)}
        >
          <FormattedMessage {...commonMessages.cancel} />
        </CustomThemeButton>
      </ButtonGroup>
    </styles.ModalFooter>
  );

  return (
    <ModalDialog
      autoFocus={!(isMergePending || canCreatePendingMerge)}
      onClose={onClose}
      onOpenComplete={() => {
        publishTrackEvent({
          action: 'viewed',
          actionSubject: 'mergeDialog',
          actionSubjectId: 'pullRequestMergeDialog',
          source: 'pullRequestScreen',
          attributes: {
            prIssuesCount: pullRequestJiraIssues.length,
          },
        });

        if (pullRequestJiraIssues.length > 0) {
          publishTrackEvent({
            action: 'viewed',
            actionSubject: 'issueTransitionForm',
            actionSubjectId: 'mergeDialogIssueTransitionForm',
            source: 'pullRequestScreen',
            attributes: {
              prIssuesCount: pullRequestJiraIssues.length,
            },
          });
        }
      }}
      width="medium"
    >
      <ModalHeader>
        <ModalTitle>
          <FormattedMessage {...messages.mergeDialogTitle} />
        </ModalTitle>
      </ModalHeader>
      <ModalBody>
        <Flex direction="column" gap="space.300">
          <MergeWarningSectionMessage />
          <PendingReviewersInfo />
          {enabledOnMergeChecksProps && (
            <ForgeEnabledOnMergeChecks {...enabledOnMergeChecksProps} />
          )}
        </Flex>
        <FakeLabel>{intl.formatMessage(messages.sourceBranch)}</FakeLabel>
        <RefBranch
          isFluidWidth
          name={isFromFork ? fullSourceBranchName : source.branch.name}
        />
        <FakeLabel>{intl.formatMessage(messages.destinationBranch)}</FakeLabel>
        <RefBranch
          isFluidWidth
          name={isFromFork ? fullDestBranchName : destination.branch.name}
        />
        {availableMergeStrategies.current.length > 1 && (
          <StrategySelect
            isBannerOpen={isBannerOpen}
            label={intl.formatMessage(messages.mergeDialogMergeStrategyLabel)}
            isDisabled={isMergePending || isRequesting}
            strategies={availableMergeStrategies.current}
            onChange={handleMergeStrategyChange}
            value={getStrategyOption(mergeStrategy, intl)}
          />
        )}
        {!shouldRemoveCommitMessageDiv && (
          <Field
            label={intl.formatMessage(messages.mergeDialogCommitMessageLabel)}
            name="merge-dialog-commit-message-textfield"
          >
            {({ fieldProps }: any) => (
              <Fragment>
                <TextArea
                  {...fieldProps}
                  isDisabled={isMergePending || isRequesting}
                  shouldFitContainer
                  minimumRows={5}
                  enableResize
                  onChange={updateCommitMessage}
                  value={message}
                  resize="auto"
                />
              </Fragment>
            )}
          </Field>
        )}
        {isMessageInvalid && mergeMessageMaxSizeBytes && (
          <ErrorMessage>
            <FormattedMessage
              {...messages.invalidMergeMessageSizeError}
              values={{ maxSizeInKiB: (mergeMessageMaxSizeBytes + 1) / 1024 }}
            />
          </ErrorMessage>
        )}
        {pullRequestJiraIssues.length > 0 &&
          !isMergePending &&
          !canCreatePendingMerge && (
            <IssueTransitionForm
              pullRequestJiraIssues={pullRequestJiraIssues}
            />
          )}

        {isErrored && (
          <styles.MergeErrorMessage>
            {errorCode === 'BUILDS_FINISHED_GO_MERGE' && (
              <CheckCircleIcon
                size="medium"
                primaryColor={token('color.icon.success', colors.G400)}
                label={errorMessage || ''}
              />
            )}
            {errorCode !== 'BUILDS_FINISHED_GO_MERGE' && (
              <WarningIcon
                size="medium"
                primaryColor={token('color.icon.warning', colors.Y300)}
                label={errorMessage || ''}
              />
            )}
            {errorMessage}
          </styles.MergeErrorMessage>
        )}
        {isOpenWarningMergeDialog && (
          <WarningMergeDialog
            createPendingMerge={createPendingMerge}
            onHideDialog={hideWarningDialog}
          />
        )}
      </ModalBody>
      <Footer />
    </ModalDialog>
  );
};
